import { useId } from 'react';
import { useGT } from 'gt-react';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { useDebounce } from 'src/hooks/useDebounce';
import {
  useForecastFactorPreview,
  type PreviewForecastFactorInput,
} from 'src/api/rpc/hooks/procurement/useForecastFactors';
import { PolicyOverrideFormWindowFields } from '../components/PolicyOverrideFormWindowFields';
import { PolicyOverrideFormActions } from '../components/PolicyOverrideFormActions';
import { formatFactorPercent } from '../components/forecastFactorFormat';
import type { ForecastFactorFormState } from './useForecastFactorForm';

// Add a growth/shrinkage factor, or edit an active one. Editing revokes the
// current row and creates its replacement, so `submit` routes to update-vs-
// create off whether the form is in edit mode. The buyer works in percent
// ("+15", "-10"); the stored value is the multiplier. The window is picked in
// whole months (the factor scales monthly buckets and is stored on UTC month
// boundaries), so the payload carries exact boundaries and the server's snap
// is a no-op. The panel owns the hook (it has to reset the form when the row
// being edited is revoked) and renders the fields.
// See docs/procurement/growth-shrinkage-factors.md.

/** How long the buyer has to stop typing before the preview asks the server. */
const PREVIEW_DEBOUNCE_MS = 300;

function PercentHint({
  factorValue,
  factorValid,
}: {
  factorValue: number | null;
  factorValid: boolean;
}) {
  const gt = useGT();
  if (factorValue !== null && factorValid) {
    return (
      <p className="mt-0.5 text-[10px] text-gray-400">
        {gt('= x{factor} on the forecast', {
          context:
            'growth factor multiplier hint, e.g. "= x1.15 on the forecast"',
          factor: String(factorValue),
        })}
      </p>
    );
  }
  return (
    <p className="mt-0.5 text-[10px] text-gray-400">
      {gt('Percent above (+) or below (-) the forecast; 0 changes nothing.', {
        context: 'growth factor percent hint',
      })}
    </p>
  );
}

// What the candidate does to the reorder point, and how much of the forecast
// horizon its window covers, before the buyer saves. Computed server-side so
// it is exactly what the save will persist.
function FactorPreview({ form }: { form: ForecastFactorFormState }) {
  const gt = useGT();
  const input: PreviewForecastFactorInput | null =
    form.canSubmit && form.factorValue !== null
      ? {
          ...form.target,
          factor: form.factorValue,
          // The whole window, start included: the preview resolves coverage
          // per bucket exactly as the save does, so leaving a bound out would
          // preview levels the save then would not persist.
          startsAt: form.window.payload.startsAt,
          endsAt: form.window.payload.endsAt,
          reviewAt: form.window.payload.reviewAt,
          excludeOverrideId: form.editing?.id ?? null,
        }
      : null;
  // Each preview aggregates the series' history and runs policy math on the
  // server, so wait for the buyer to stop typing rather than asking once per
  // keystroke. Debounced as a string: `input` is a fresh object every render,
  // and a debounce keyed on its identity would never settle.
  const settledKey = useDebounce(
    input === null ? null : JSON.stringify(input),
    PREVIEW_DEBOUNCE_MS,
  );
  const preview = useForecastFactorPreview(
    settledKey === null
      ? null
      : (JSON.parse(settledKey) as PreviewForecastFactorInput),
  );
  if (!form.canSubmit || !preview.data) return null;
  const { before, after, coveredBuckets, horizonBuckets } = preview.data;
  return (
    <p className="mt-1 text-xs text-gray-500">
      {gt(
        'Reorder point {before} to {after}; covers {covered} of {total} forecast months.',
        {
          context: 'growth factor preview line',
          before: Math.round(before.reorderPoint),
          after: Math.round(after.reorderPoint),
          covered: coveredBuckets,
          total: horizonBuckets,
        },
      )}
    </p>
  );
}

export function ForecastFactorForm({
  form,
}: {
  form: ForecastFactorFormState;
}) {
  const gt = useGT();
  const fieldId = useId();
  const { editing } = form;
  return (
    <>
      {editing ? (
        <p className="mb-2 text-xs text-blue-700">
          {gt('Editing the {pct} factor. Saving replaces it with a new row.', {
            context: 'growth factor edit mode notice',
            pct: formatFactorPercent(editing.factor),
          })}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label
            htmlFor={`${fieldId}-percent`}
            className="text-xs text-gray-500"
          >
            {gt('Growth / shrinkage (%)', {
              context: 'growth factor percent field',
            })}
          </Label>
          <Input
            id={`${fieldId}-percent`}
            type="number"
            inputMode="decimal"
            step={0.1}
            value={form.percent}
            onChange={(e) => form.setPercent(e.target.value)}
            aria-invalid={
              (form.percent !== '' && !form.factorValid) || undefined
            }
          />
          <PercentHint
            factorValue={form.factorValue}
            factorValid={form.factorValid}
          />
        </div>
        <div>
          <Label
            htmlFor={`${fieldId}-reason`}
            className="text-xs text-gray-500"
          >
            {gt('Reason', { context: 'growth factor reason field' })}
          </Label>
          <Textarea
            id={`${fieldId}-reason`}
            rows={1}
            value={form.window.reason}
            onChange={(e) => form.window.setReason(e.target.value)}
          />
        </div>
        <PolicyOverrideFormWindowFields
          grain="month"
          window={form.window}
          pending={form.pending}
        />
      </div>

      <FactorPreview form={form} />

      <PolicyOverrideFormActions
        editing={editing != null}
        canSubmit={form.canSubmit}
        pending={form.pending}
        onSubmit={form.submit}
        onReset={form.reset}
        saveLabel={gt('Save changes', {
          context: 'growth factor submit button',
        })}
        addLabel={gt('Add factor', { context: 'growth factor submit button' })}
      />
    </>
  );
}
